import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SURFACES } from "@/lib/physics";
import type { ForceConfig } from "@/lib/physics";
import { Play, Pause, RotateCcw } from "lucide-react";

interface ForceControlsProps {
	config: ForceConfig;
	onConfigChange: (config: Partial<ForceConfig>) => void;
	isRunning: boolean;
	onToggleRun: () => void;
	onReset: () => void;
	showAppliedForce?: boolean;
	showMass?: boolean;
	showFriction?: boolean;
	showSurfaceSelect?: boolean;
	selectedSurface?: string;
	onSurfaceChange?: (surface: string) => void;
	netForce?: number;
	frictionForce?: number;
}

export function ForceControls({
	config,
	onConfigChange,
	isRunning,
	onToggleRun,
	onReset,
	showAppliedForce = true,
	showMass = true,
	showFriction = true,
	showSurfaceSelect = false,
	selectedSurface,
	onSurfaceChange,
	netForce,
	frictionForce,
}: ForceControlsProps) {
	return (
		<div className="space-y-4">
			<div className="flex gap-2">
				<Button
					onClick={onToggleRun}
					variant={isRunning ? "secondary" : "default"}
					className="flex items-center gap-2"
				>
					{isRunning ? (
						<>
							<Pause className="w-4 h-4" /> Pause
						</>
					) : (
						<>
							<Play className="w-4 h-4" /> Play
						</>
					)}
				</Button>
				<Button onClick={onReset} variant="outline" className="flex items-center gap-2">
					<RotateCcw className="w-4 h-4" /> Reset
				</Button>
			</div>

			{showAppliedForce && (
				<div className="space-y-2">
					<div className="flex items-center justify-between">
						<Label className="text-sm font-medium">Applied Force</Label>
						<Badge variant="secondary" className="font-mono">
							{config.appliedForce.toFixed(0)} N
						</Badge>
					</div>
					<Slider
						value={[config.appliedForce]}
						onValueChange={([v]) => onConfigChange({ appliedForce: v })}
						min={-100}
						max={100}
						step={1}
						className="w-full"
					/>
					<p className="text-xs text-muted-foreground">
						Negative = left, Positive = right
					</p>
				</div>
			)}

			{showMass && (
				<div className="space-y-2">
					<div className="flex items-center justify-between">
						<Label className="text-sm font-medium">Mass</Label>
						<Badge variant="secondary" className="font-mono">
							{config.mass.toFixed(1)} kg
						</Badge>
					</div>
					<Slider
						value={[config.mass]}
						onValueChange={([v]) => onConfigChange({ mass: v })}
						min={0.5}
						max={50}
						step={0.5}
						className="w-full"
					/>
				</div>
			)}

			{showFriction && !showSurfaceSelect && (
				<div className="space-y-2">
					<div className="flex items-center justify-between">
						<Label className="text-sm font-medium">
							Friction Coefficient (μ)
						</Label>
						<Badge variant="secondary" className="font-mono">
							{config.frictionCoefficient.toFixed(2)}
						</Badge>
					</div>
					<Slider
						value={[config.frictionCoefficient]}
						onValueChange={([v]) =>
							onConfigChange({ frictionCoefficient: v })
						}
						min={0}
						max={1}
						step={0.01}
						className="w-full"
					/>
				</div>
			)}

			{showSurfaceSelect && onSurfaceChange && (
				<div className="space-y-2">
					<Label className="text-sm font-medium">Surface Type</Label>
					<Select
						value={selectedSurface}
						onValueChange={(val) => {
							onSurfaceChange(val);
							onConfigChange({
								frictionCoefficient: SURFACES[val].coefficient,
							});
						}}
					>
						<SelectTrigger>
							<SelectValue placeholder="Select surface" />
						</SelectTrigger>
						<SelectContent>
							{Object.entries(SURFACES).map(([key, s]) => (
								<SelectItem key={key} value={key}>
									{s.label} (μ = {s.coefficient})
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			)}

			{(netForce !== undefined || frictionForce !== undefined) && (
				<div className="grid grid-cols-2 gap-2 p-3 bg-muted/50 rounded-lg text-sm">
					{frictionForce !== undefined && (
						<div>
							<span className="text-muted-foreground">Friction:</span>{" "}
							<span className="font-mono text-red-400">
								{Math.abs(frictionForce).toFixed(1)} N
							</span>
						</div>
					)}
					{netForce !== undefined && (
						<div>
							<span className="text-muted-foreground">Net Force:</span>{" "}
							<span className="font-mono text-amber-400">
								{netForce.toFixed(1)} N
							</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
